import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import ImageUploader from '../../components/ImageUploader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProductsPage() {
    const { theme } = useTheme();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        nom: '',
        description: '',
        prix: '',
        stock: '',
        marque: '',
        images: []
    });

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API}/products`);
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const openCreateDialog = () => {
        setEditingProduct(null);
        setFormData({ nom: '', description: '', prix: '', stock: '', marque: '', images: [] });
        setShowDialog(true);
    };

    const openEditDialog = (product) => {
        setEditingProduct(product);
        setFormData({
            nom: product.nom,
            description: product.description,
            prix: product.prix.toString(),
            stock: product.stock.toString(),
            marque: product.marque,
            images: product.images || []
        });
        setShowDialog(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            prix: parseFloat(formData.prix),
            stock: parseInt(formData.stock),
            images: formData.images
        };

        try {
            if (editingProduct) {
                await axios.put(`${API}/products/${editingProduct.id}`, payload);
                toast.success('Produit modifié');
            } else {
                await axios.post(`${API}/products`, payload);
                toast.success('Produit créé');
            }
            setShowDialog(false);
            fetchProducts();
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce produit ?')) return;
        try {
            await axios.delete(`${API}/products/${id}`);
            toast.success('Produit supprimé');
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const formatPrice = (price) => new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF' }).format(price);

    return (
        <div data-testid="admin-products-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className={`font-heading text-2xl font-medium ${theme === 'dark' ? 'text-white' : 'text-mmd-navy'}`}>Produits</h1>
                    <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>Gérez votre catalogue de matériel de bureau</p>
                </div>
                <Button onClick={openCreateDialog} data-testid="add-product-btn" className={theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'}>
                    <Plus size={18} className="mr-2" />
                    Ajouter
                </Button>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className={`rounded-lg p-4 animate-pulse h-24 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`} />
                    ))}
                </div>
            ) : products.length === 0 ? (
                <div className={`text-center py-12 ${theme === 'dark' ? 'text-mmd-silver/50' : 'text-mmd-navy/50'}`}>
                    <p>Aucun produit</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {products.map(product => (
                        <div key={product.id} className={`rounded-lg p-4 flex items-center gap-4 border ${theme === 'dark' ? 'bg-mmd-surface-dark border-white/10' : 'bg-white border-black/10'}`} data-testid={`admin-product-${product.id}`}>
                            <img
                                src={product.images?.[0] || 'https://via.placeholder.com/80x80?text=No+Image'}
                                alt={product.nom}
                                className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div className="flex-1 min-w-0">
                                <h3 className={`font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-mmd-navy'}`}>{product.nom}</h3>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/60' : 'text-mmd-navy/60'}`}>{product.marque}</p>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy font-semibold'}`}>{formatPrice(product.prix)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-1 rounded ${product.stock > 0 ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-600'}`}>
                                    Stock: {product.stock}
                                </span>
                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(product)} data-testid={`edit-product-${product.id}`}>
                                    <Pencil size={18} className={theme === 'dark' ? 'text-mmd-silver' : 'text-mmd-navy/60'} />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`}>
                                    <Trash size={18} className="text-red-500" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div>
                            <Label htmlFor="nom">Nom</Label>
                            <Input id="nom" name="nom" value={formData.nom} onChange={handleInputChange} required data-testid="product-form-nom" />
                        </div>
                        <div>
                            <Label htmlFor="marque">Marque</Label>
                            <Input id="marque" name="marque" value={formData.marque} onChange={handleInputChange} required data-testid="product-form-marque" />
                        </div>
                        <div>
                            <Label htmlFor="description">Description</Label>
                            <Textarea id="description" name="description" value={formData.description} onChange={handleInputChange} rows={3} required data-testid="product-form-description" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="prix">Prix (FCFA)</Label>
                                <Input id="prix" name="prix" type="number" value={formData.prix} onChange={handleInputChange} required data-testid="product-form-prix" />
                            </div>
                            <div>
                                <Label htmlFor="stock">Stock</Label>
                                <Input id="stock" name="stock" type="number" value={formData.stock} onChange={handleInputChange} required data-testid="product-form-stock" />
                            </div>
                        </div>
                        <div>
                            <Label className="mb-2 block">Images du produit</Label>
                            <ImageUploader 
                                images={formData.images} 
                                onImagesChange={(images) => setFormData(prev => ({ ...prev, images }))}
                                maxImages={5}
                            />
                        </div>
                        <Button type="submit" className={`w-full ${theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'}`} data-testid="product-form-submit">
                            {editingProduct ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
